import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import API_BASE_URL from '../Config/config.jsx';
import {
  ChevronDown, ChevronRight, Search, RefreshCw,
  AlertCircle, ArrowUpDown, ArrowUp, ArrowDown,
  BarChart2, Users, Activity, TrendingUp, Trash2, MoreVertical
} from 'lucide-react';
import CollegeHeader from '../BasicComponents/CollegeHeader';
import CollegeHomeSideBar from '../BasicComponents/CollegeHomeSideBar';
import CollegeFooter from '../BasicComponents/CollegeFooter';

// ─── Constants ─────────────────────────────────────────────────────────────

const FEATURES = ['All', 'MockInterview', 'BootCamp', 'TSG', 'LaunchPad', 'TechExplorer'];

const FEATURE_COLORS = {
  MockInterview: 'var(--primary-blue, #4F46E5)',
  BootCamp:      '#7C3AED',
  TSG:           '#D97706',
  LaunchPad:     '#059669',
  TechExplorer:  '#0891B2',
};

const ALL_FEATURE_BREAKDOWN = [
  { label: 'MockInterview', key: 'mock_count',      accent: 'var(--primary-blue, #4F46E5)' },
  { label: 'BootCamp',      key: 'bootcamp_count',  accent: '#7C3AED' },
  { label: 'LaunchPad',     key: 'launchpad_count', accent: '#059669' },
  { label: 'TSG',           key: 'tsg_count',       accent: '#D97706' },
  { label: 'TechExplorer',  key: 'tech_count',      accent: '#0891B2' },
];

// ─── Design Tokens ──────────────────────────────────────────────────────────

const T = {
  border:  '1px solid #E2E8F0',
  borderMd: '1px solid #CBD5E1',
  radius:  { sm: '6px', md: '10px', lg: '14px', xl: '18px', xxl: '22px', full: '999px' },
  shadow:  {
    xs: '0 1px 3px rgba(0,0,0,0.05)',
    sm: '0 2px 8px rgba(0,0,0,0.07)',
    md: '0 4px 20px rgba(0,0,0,0.08)',
  },
  brand:       'var(--primary-blue, #4F46E5)',
  brandLight:  'rgba(79, 70, 229,0.08)',
  brandBorder: 'rgba(79, 70, 229,0.18)',
  text: {
    heading:  '#0F172A',
    body:     '#334155',
    muted:    '#64748B',
    disabled: '#94A3B8',
  },
  bg: { main: '#F8FAFC', section: '#FFFFFF' },
  status: {
    completed: { background: '#F0FDF4', color: '#16A34A', border: '1px solid #BBF7D0' },
    active:    { background: '#EFF6FF', color: 'var(--secondary-blue, #1E3A8A)', border: '1px solid #BFDBFE' },
    viewed:    { background: '#F5F3FF', color: '#7C3AED', border: '1px solid #DDD6FE' },
    default:   { background: '#F8FAFC', color: '#64748B', border: '1px solid #E2E8F0' },
  },
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

const fmtDate = (d) => {
  if (!d) return '-';
  return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

const safeInt   = (v) => { const n = parseInt(v, 10);   return isNaN(n) ? 0    : n; };
const safeFloat = (v) => { const n = parseFloat(v);      return isNaN(n) ? null : n; };

const truncateId = (id) => {
  if (!id) return '-';
  return String(id);
};

// ─── Sub-components ──────────────────────────────────────────────────────────

const StatusBadge = ({ status }) => {
  if (!status) return <span style={{ color: T.text.disabled, fontSize: '12px' }}>No activity</span>;
  const key  = status.toLowerCase();
  const sty  = T.status[key] || T.status.default;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 5,
      padding: '3px 10px', borderRadius: T.radius.full,
      fontSize: '11px', fontWeight: 700, textTransform: 'uppercase',
      letterSpacing: '0.04em', whiteSpace: 'nowrap', ...sty,
    }}>
      <span style={{ width: 5, height: 5, borderRadius: '50%', background: sty.color, flexShrink: 0 }} />
      {status}
    </span>
  );
};

const ScoreBar = ({ score }) => {
  const pct = safeFloat(score);
  if (pct === null) return <span style={{ color: T.text.disabled, fontSize: '12px' }}>-</span>;
  const r = Math.round(pct);
  const barColor  = r >= 80 ? '#22C55E' : r >= 60 ? '#F59E0B' : '#EF4444';
  const textColor = r >= 80 ? '#16A34A' : r >= 60 ? '#CA8A04' : '#DC2626';
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <div style={{ width: 52, height: 4, background: '#E2E8F0', borderRadius: 99, overflow: 'hidden', flexShrink: 0 }}>
        <div style={{ height: '100%', width: `${Math.min(r, 100)}%`, background: barColor, borderRadius: 99 }} />
      </div>
      <span style={{ fontSize: '12px', fontWeight: 700, color: textColor, minWidth: 28 }}>{r}%</span>
    </div>
  );
};

const SortIcon = ({ field, sortField, sortDir }) => {
  const style = { width: 11, height: 11, marginLeft: 3, verticalAlign: 'middle', display: 'inline' };
  if (sortField !== field) return <ArrowUpDown style={{ ...style, opacity: 0.25 }} />;
  return sortDir === 'asc'
    ? <ArrowUp   style={{ ...style, color: T.brand }} />
    : <ArrowDown style={{ ...style, color: T.brand }} />;
};

const TH_BASE = {
  padding: '11px 16px', fontSize: '11px', fontWeight: 700,
  textTransform: 'uppercase', letterSpacing: '0.05em',
  color: T.text.muted, whiteSpace: 'nowrap',
  background: T.bg.main, borderBottom: T.border,
  textAlign: 'left',
};

const SortableHeader = ({ label, field, sortField, sortDir, onSort, style = {} }) => (
  <th
    onClick={() => onSort(field)}
    style={{ ...TH_BASE, ...style, cursor: 'pointer', userSelect: 'none' }}
  >
    {label}
    <SortIcon field={field} sortField={sortField} sortDir={sortDir} />
  </th>
);

// Skeleton
const Skeleton = ({ w = 80, h = 12 }) => (
  <div style={{ width: w, height: h, background: '#E2E8F0', borderRadius: 6, animation: 'pulse 1.4s ease-in-out infinite' }} />
);

const SkeletonRow = ({ cols }) => (
  <tr style={{ borderBottom: T.border }}>
    <td style={{ padding: '14px 16px', width: 36 }} />
    <td style={{ padding: '14px 16px' }}>
      <Skeleton w={120} /><div style={{ marginTop: 6 }}><Skeleton w={150} h={10} /></div>
    </td>
    <td style={{ padding: '14px 16px' }}><Skeleton w={72} /></td>
    <td style={{ padding: '14px 16px' }}><Skeleton w={40} /></td>
    {cols > 5 && <td style={{ padding: '14px 16px' }}><Skeleton w={60} /></td>}
    <td style={{ padding: '14px 16px' }}><Skeleton w={80} /></td>
    <td style={{ padding: '14px 16px' }}><Skeleton w={64} /></td>
  </tr>
);

// --- Feature breakdown (All tab expand) --------------------------------------

const FeatureBreakdown = ({ student }) => {
  const total = safeInt(student.total_activities);
  return (
    <div style={{ padding: '20px 24px 20px 56px', background: '#FAFBFC', borderTop: T.border }}>
      <p style={{ fontSize: '10px', fontWeight: 700, color: T.text.muted, textTransform: 'uppercase', letterSpacing: '0.07em', margin: '0 0 14px' }}>
        Feature Activity Breakdown
      </p>
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
        {ALL_FEATURE_BREAKDOWN.map(({ label, key, accent }) => {
          const count = safeInt(student[key]);
          const active = count > 0;
          return (
            <div key={key} style={{
              display: 'flex', alignItems: 'center', gap: 10,
              padding: '10px 16px', borderRadius: T.radius.lg,
              background: active ? `${accent}0D` : T.bg.main,
              border: `1px solid ${active ? `${accent}30` : '#E2E8F0'}`,
              minWidth: 120,
            }}>
              <span style={{
                fontSize: '18px', fontWeight: 800, color: active ? accent : T.text.disabled,
                lineHeight: 1, minWidth: 24, textAlign: 'center',
              }}>
                {count}
              </span>
              <span style={{ fontSize: '12px', color: active ? T.text.body : T.text.disabled, fontWeight: 600 }}>
                {label}
              </span>
            </div>
          );
        })}
      </div>
      {total === 0 && (
        <p style={{ fontSize: '12px', color: T.text.muted, marginTop: 12, fontStyle: 'italic' }}>
          No activity recorded yet.
        </p>
      )}
    </div>
  );
};

// --- Logs panel (feature tab expand) -----------------------------------------

const LogsPanel = ({ student }) => {
  const logs = Array.isArray(student.logs) ? student.logs : [];

  if (logs.length === 0) return (
    <div style={{ padding: '24px', background: '#FAFBFC', borderTop: T.border, textAlign: 'center' }}>
      <p style={{ fontSize: '13px', color: T.text.muted, margin: 0 }}>No session logs available.</p>
    </div>
  );

  const LOG_TH = {
    padding: '10px 14px', fontSize: '10px', fontWeight: 700,
    textTransform: 'uppercase', letterSpacing: '0.05em',
    color: T.text.muted, background: T.bg.main,
    borderBottom: T.border, whiteSpace: 'nowrap', textAlign: 'left',
  };

  return (
    <div style={{ padding: '16px 24px 20px 56px', background: '#FAFBFC', borderTop: T.border }}>
      <p style={{ fontSize: '10px', fontWeight: 700, color: T.text.muted, textTransform: 'uppercase', letterSpacing: '0.07em', margin: '0 0 12px' }}>
        {logs.length} session{logs.length !== 1 ? 's' : ''} &middot; {student.feature_name}
      </p>
      <div style={{ background: T.bg.section, border: T.border, borderRadius: T.radius.lg, overflow: 'hidden', boxShadow: T.shadow.xs }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
            <thead>
              <tr>
                <th style={{ ...LOG_TH, width: 32 }}>#</th>
                <th style={LOG_TH}>Detail</th>
                <th style={LOG_TH}>Score / Metric</th>
                <th style={LOG_TH}>Status</th>
                <th style={LOG_TH}>Last Active</th>
                <th style={LOG_TH}>Last Action</th>
              </tr>
            </thead>
            <tbody>
              {logs.map((log, idx) => {
                const isLatest = idx === 0;
                const score    = safeFloat(log.score);
                const metric   = log.metric != null ? safeInt(log.metric) : null;
                return (
                  <tr
                    key={idx}
                    style={{
                      borderBottom: T.border,
                      background: isLatest ? 'rgba(79, 70, 229,0.025)' : T.bg.section,
                    }}
                  >
                    <td style={{ padding: '10px 14px', color: T.text.disabled, fontFamily: 'monospace', fontSize: '11px' }}>
                      {idx + 1}
                    </td>
                    <td style={{ padding: '10px 14px', maxWidth: 220 }}>
                      <span
                        style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: T.text.body, fontWeight: 500 }}
                        title={log.feature_detail || undefined}
                      >
                        {log.feature_detail || '-'}
                      </span>
                      {isLatest && (
                        <span style={{ fontSize: '9px', color: T.brand, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                          Latest
                        </span>
                      )}
                    </td>
                    <td style={{ padding: '10px 14px' }}>
                      {score !== null ? <ScoreBar score={score} />
                        : metric !== null ? <span style={{ color: T.text.body, fontWeight: 600 }}>{metric}</span>
                        : <span style={{ color: T.text.disabled }}>-</span>}
                    </td>
                    <td style={{ padding: '10px 14px' }}><StatusBadge status={log.status} /></td>
                    <td style={{ padding: '10px 14px', whiteSpace: 'nowrap', color: T.text.muted }}>{fmtDate(log.last_active)}</td>
                    <td style={{ padding: '10px 14px', maxWidth: 160 }}>
                      <span
                        style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: T.text.muted }}
                        title={log.last_action || undefined}
                      >
                        {log.last_action || '-'}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// --- Main Component -----------------------------------------------------------

const CollegeAnalyticsDashboard = () => {
  const location = useLocation();
  const [user] = useState(() => {
    try { return JSON.parse(localStorage.getItem('user')); } catch { return null; }
  });

  const collegeName = user?.college_name || user?.name || localStorage.getItem('college_name');
  // College admin user object stores identity as id or username (not userId/user_id)
  const adminId = user?.id || user?.username || user?.userId || user?.user_id;

  // -- filters & pagination --------------------------------------------------
  const [activeTab,  setActiveTab]  = useState(location.state?.activeTab || 'All');
  const [search,     setSearch]     = useState('');
  const [status,     setStatus]     = useState('');
  const [startDate,  setStartDate]  = useState('');
  const [endDate,    setEndDate]    = useState('');
  const [page,       setPage]       = useState(1);
  const [removingId, setRemovingId] = useState(null);
  const limit = 15;

  // ... (rest of the component)

  const handleRemoveEnrollment = async (enrollmentId, studentName) => {
    if (!window.confirm(`Are you sure you want to remove ${studentName}'s enrollment? This will hide it from the student's view immediately.`)) {
      return;
    }

    const reason = window.prompt("Reason for removal (optional):", "Removed by College Admin");
    
    if (!enrollmentId) {
      console.error('[handleRemoveEnrollment] No enrollmentId provided - cannot remove');
      alert('Cannot remove: enrollment ID is missing. Please refresh the page and try again.');
      return;
    }

    try {
      setRemovingId(enrollmentId);
      console.log(`[handleRemoveEnrollment] Initiating removal for ID: ${enrollmentId}, student: ${studentName}`);

      const res = await axios.patch(`${API_BASE_URL}/college/analytics/bootcamps/${enrollmentId}/remove`, {
        reason: reason || 'Removed by College Admin',
        admin_id: adminId,
        college_name: collegeName,
      });

      console.log(`[handleRemoveEnrollment] Response:`, res.data);

      if (res.data.success) {
        alert("Enrollment removed successfully");
        fetchAnalytics(); // Refresh the list
      }
    } catch (err) {
      console.error("[handleRemoveEnrollment] Error removing enrollment:", err);
      const errorMsg = err.response?.data?.message || err.response?.data?.error || "Failed to remove enrollment";
      console.error(`[handleRemoveEnrollment] Server error details: ${errorMsg}`);
      
      if (err.response?.status === 404 && !enrollmentId) {
        alert("Invalid Enrollment ID. Please try refreshing the page.");
      } else {
        alert(errorMsg);
      }
    } finally {
      setRemovingId(null);
    }
  };

  // -- data ------------------------------------------------------------------
  const [students,   setStudents]   = useState([]);
  const [total,      setTotal]      = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [loading,    setLoading]    = useState(true);
  const [error,      setError]      = useState(null);

  // -- UI --------------------------------------------------------------------
  const [expandedIds, setExpandedIds] = useState(new Set());
  const [sortField,   setSortField]   = useState('last_active');
  const [sortDir,     setSortDir]     = useState('desc');

  // -- fetch -----------------------------------------------------------------
  const fetchAnalytics = useCallback(async () => {
    if (!collegeName) return;
    setLoading(true);
    setError(null);
    setExpandedIds(new Set());
    try {
      const res = await axios.get(`${API_BASE_URL}/college/analytics/students`, {
        params: {
          college_name: collegeName,
          feature:   activeTab === 'All' ? undefined : activeTab,
          search:    search    || undefined,
          status:    status    || undefined,
          startDate: startDate || undefined,
          endDate:   endDate   || undefined,
          page, limit,
        },
      });
      const rows = res.data.students || [];
      const tot  = res.data.total    || 0;
      setStudents(rows);
      setTotal(tot);
      setTotalPages(Math.max(1, Math.ceil(tot / limit)));
    } catch (err) {
      console.error('[Analytics] fetch error:', err);
      setError('Failed to load analytics data.');
    } finally {
      setLoading(false);
    }
  }, [collegeName, activeTab, search, status, startDate, endDate, page, limit]);

  useEffect(() => { fetchAnalytics(); }, [fetchAnalytics]);

  // -- handlers --------------------------------------------------------------
  const handleTabChange = (tab) => { setActiveTab(tab); setPage(1); };

  const handleSort = (field) => {
    setSortField((prev) => {
      if (prev === field) setSortDir((d) => d === 'asc' ? 'desc' : 'asc');
      else setSortDir('desc');
      return field;
    });
  };

  const toggleExpand = (uid) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      next.has(uid) ? next.delete(uid) : next.add(uid);
      return next;
    });
  };

  const focusInput = (e) => {
    e.target.style.borderColor = T.brand;
    e.target.style.boxShadow   = `0 0 0 3px ${T.brandLight}`;
  };
  const blurInput = (e) => {
    e.target.style.borderColor = '#E2E8F0';
    e.target.style.boxShadow   = 'none';
  };

  // -- derived ---------------------------------------------------------------
  const isAllTab = activeTab === 'All';

  const activeStudents = useMemo(
    () => students.filter(s => safeInt(isAllTab ? s.total_activities : s.total_logs) > 0).length,
    [students, isAllTab]
  );

  const sortedStudents = useMemo(() => {
    if (!students.length) return students;
    return [...students].sort((a, b) => {
      let av, bv;
      switch (sortField) {
        case 'name':
          av = (a.first_name || '').toLowerCase();
          bv = (b.first_name || '').toLowerCase();
          return sortDir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
        case 'total':
          av = safeInt(isAllTab ? a.total_activities : a.total_logs);
          bv = safeInt(isAllTab ? b.total_activities : b.total_logs);
          break;
        case 'score':
          av = safeFloat(a.avg_score) ?? -1;
          bv = safeFloat(b.avg_score) ?? -1;
          break;
        case 'status':
          av = a.overall_status || '';
          bv = b.overall_status || '';
          return sortDir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
        case 'last_active':
        default:
          av = new Date(a.last_active || 0).getTime();
          bv = new Date(b.last_active || 0).getTime();
          break;
      }
      return sortDir === 'asc' ? av - bv : bv - av;
    });
  }, [students, sortField, sortDir, isAllTab]);

  const colCount   = isAllTab ? 6 : 7;
  const tabAccent  = FEATURE_COLORS[activeTab] || T.brand;
  const hasFilters = !!(search || status || startDate || endDate);

  // -- input style -----------------------------------------------------------
  const inputStyle = {
    width: '100%', padding: '9px 12px', fontSize: '13px', fontWeight: 500,
    background: T.bg.section, border: T.border, borderRadius: T.radius.md,
    color: T.text.body, outline: 'none',
    transition: 'border-color 0.15s, box-shadow 0.15s',
    boxSizing: 'border-box',
  };

  const labelStyle = {
    display: 'block', fontSize: '10px', fontWeight: 700,
    color: T.text.muted, textTransform: 'uppercase',
    letterSpacing: '0.06em', marginBottom: '5px',
  };

  // ────────────────────────────────────────────────────────────────────────
  return (
    <div className="college-main-layout" style={{ background: T.bg.main, fontFamily: 'inherit' }}>
      <CollegeHeader />

      <div className="college-content-wrapper">
        <CollegeHomeSideBar />

        <main className="college-main-content">
          <div style={{ maxWidth: 1200, margin: '0 auto', padding: '32px 24px 80px' }}>

            {/* ── Page header ────────────────────────────────────────────── */}
            <div style={{
              background: T.bg.section, border: T.border, borderRadius: T.radius.xxl,
              overflow: 'hidden', boxShadow: T.shadow.sm, marginBottom: 20,
            }}>
              <div style={{ height: 4, background: 'linear-gradient(90deg, var(--primary-blue, #4F46E5) 0%, #7C3AED 60%, #0891B2 100%)' }} />
              <div style={{
                padding: '22px 28px',
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                gap: 16, flexWrap: 'wrap',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                  <div style={{
                    width: 44, height: 44, borderRadius: T.radius.lg,
                    background: T.brandLight, border: `1px solid ${T.brandBorder}`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                  }}>
                    <BarChart2 size={20} style={{ color: T.brand }} />
                  </div>
                  <div>
                    <h1 style={{ fontSize: '1.375rem', fontWeight: 800, margin: 0, color: T.text.heading, letterSpacing: '-0.02em', lineHeight: 1.2 }}>
                      Student Analytics
                    </h1>
                    <p style={{ fontSize: '13px', color: T.text.muted, margin: '3px 0 0', fontWeight: 500 }}>
                      {collegeName || 'Institution'}
                      {!loading && (
                        <> &middot; <span style={{ fontWeight: 700, color: T.text.body }}>{total}</span> student{total !== 1 ? 's' : ''}{!isAllTab ? ` &middot; ${activeTab}` : ''}</>
                      )}
                    </p>
                  </div>
                </div>

                <button
                  onClick={fetchAnalytics}
                  disabled={loading}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: 6,
                    padding: '8px 16px', borderRadius: T.radius.md,
                    background: T.bg.main, border: T.border,
                    fontSize: '13px', fontWeight: 600,
                    color: T.text.muted, cursor: loading ? 'not-allowed' : 'pointer',
                    opacity: loading ? 0.6 : 1, transition: 'all 0.15s',
                    boxShadow: T.shadow.xs,
                  }}
                  onMouseEnter={e => { if (!loading) { e.currentTarget.style.background = '#F1F5F9'; e.currentTarget.style.color = T.text.body; }}}
                  onMouseLeave={e => { e.currentTarget.style.background = T.bg.main; e.currentTarget.style.color = T.text.muted; }}
                >
                  <RefreshCw size={13} style={{ animation: loading ? 'spin 0.8s linear infinite' : 'none' }} />
                  Refresh
                </button>
              </div>
            </div>

            {/* ── Stat cards ────────────────────────────────────────────────── */}
            {!loading && (
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
                gap: 12, marginBottom: 20,
              }}>
                {[
                  { label: 'Total Students',   value: total,                    icon: Users,      accent: 'var(--primary-blue, #4F46E5)', accentBg: 'rgba(79, 70, 229,0.08)'  },
                  { label: 'With Activity',     value: activeStudents,           icon: Activity,   accent: '#16A34A', accentBg: 'rgba(22,163,74,0.08)'   },
                  { label: 'No Activity',       value: total - activeStudents,   icon: Users,      accent: '#94A3B8', accentBg: 'rgba(148,163,184,0.10)' },
                  {
                    label: isAllTab ? 'Features Available' : `${activeTab} Active`,
                    value: isAllTab ? FEATURES.length - 1 : activeStudents,
                    icon: TrendingUp, accent: tabAccent,
                    accentBg: `${tabAccent}14`,
                  },
                ].map((s, i) => {
                  const Icon = s.icon;
                  return (
                    <div key={i} style={{
                      background: T.bg.section, border: T.border, borderRadius: T.radius.xl,
                      padding: '16px 18px', boxShadow: T.shadow.xs,
                      display: 'flex', alignItems: 'center', gap: 12,
                    }}>
                      <div style={{
                        width: 38, height: 38, borderRadius: T.radius.md,
                        background: s.accentBg, flexShrink: 0,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}>
                        <Icon size={17} style={{ color: s.accent }} />
                      </div>
                      <div>
                        <p style={{ fontSize: '10px', fontWeight: 700, color: T.text.muted, textTransform: 'uppercase', letterSpacing: '0.06em', margin: '0 0 3px' }}>
                          {s.label}
                        </p>
                        <p style={{ fontSize: '20px', fontWeight: 800, color: T.text.heading, margin: 0, letterSpacing: '-0.02em', lineHeight: 1 }}>
                          {s.value}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* ── Feature tabs ────────────────────────────────────────────────── */}
            <div style={{
              background: T.bg.section, border: T.border, borderRadius: T.radius.xl,
              padding: '6px', display: 'flex', gap: 2, flexWrap: 'wrap',
              boxShadow: T.shadow.xs, marginBottom: 16,
            }}>
              {FEATURES.map((f) => {
                const isActive = activeTab === f;
                const fColor   = FEATURE_COLORS[f] || T.brand;
                return (
                  <button
                    key={f}
                    onClick={() => handleTabChange(f)}
                    style={{
                      padding: '7px 16px', borderRadius: T.radius.lg,
                      fontSize: '13px', fontWeight: isActive ? 700 : 500,
                      border: isActive ? `1px solid ${f === 'All' ? T.brandBorder : fColor + '30'}` : '1px solid transparent',
                      cursor: 'pointer', transition: 'all 0.15s', outline: 'none',
                      background: isActive ? (f === 'All' ? T.brandLight : `${fColor}12`) : 'transparent',
                      color: isActive ? (f === 'All' ? T.brand : fColor) : T.text.muted,
                    }}
                    onMouseEnter={e => { if (!isActive) { e.currentTarget.style.background = T.bg.main; e.currentTarget.style.color = T.text.body; }}}
                    onMouseLeave={e => { if (!isActive) { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = T.text.muted; }}}
                  >
                    {f}
                  </button>
                );
              })}
            </div>

            {/* -- Filter bar ------------------------------------------------- */}
            <div style={{
              background: T.bg.section, border: T.border, borderRadius: T.radius.xl,
              padding: '16px 20px', boxShadow: T.shadow.xs, marginBottom: 16,
            }}>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
                gap: 14, alignItems: 'end',
              }}>
                {/* Search */}
                <div>
                  <label style={labelStyle}>Search</label>
                  <div style={{ position: 'relative' }}>
                    <Search style={{
                      position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)',
                      width: 13, height: 13, color: T.text.disabled, pointerEvents: 'none',
                    }} />
                    <input
                      type="text"
                      placeholder="Name, email, ID..."
                      value={search}
                      onChange={e => { setSearch(e.target.value); setPage(1); }}
                      onFocus={focusInput} onBlur={blurInput}
                      style={{ ...inputStyle, paddingLeft: 30 }}
                    />
                  </div>
                </div>

                {/* Status */}
                <div>
                  <label style={labelStyle}>Status</label>
                  <select
                    value={status}
                    onChange={e => { setStatus(e.target.value); setPage(1); }}
                    onFocus={focusInput} onBlur={blurInput}
                    style={{ ...inputStyle, appearance: 'none', WebkitAppearance: 'none', paddingRight: 28 }}
                  >
                    <option value="">All Statuses</option>
                    <option value="active">Active</option>
                    <option value="completed">Completed</option>
                    <option value="viewed">Viewed</option>
                  </select>
                </div>

                {/* Date range */}
                <div>
                  <label style={labelStyle}>From</label>
                  <input
                    type="date" value={startDate}
                    onChange={e => { setStartDate(e.target.value); setPage(1); }}
                    onFocus={focusInput} onBlur={blurInput}
                    style={inputStyle}
                  />
                </div>
                <div>
                  <label style={labelStyle}>To</label>
                  <input
                    type="date" value={endDate}
                    onChange={e => { setEndDate(e.target.value); setPage(1); }}
                    onFocus={focusInput} onBlur={blurInput}
                    style={inputStyle}
                  />
                </div>

                {/* Clear filters */}
                {hasFilters && (
                  <div style={{ display: 'flex', alignItems: 'flex-end' }}>
                    <button
                      onClick={() => { setSearch(''); setStatus(''); setStartDate(''); setEndDate(''); setPage(1); }}
                      style={{
                        padding: '9px 14px', borderRadius: T.radius.md,
                        background: T.bg.main, border: T.border,
                        fontSize: '12px', fontWeight: 600, color: T.text.muted,
                        cursor: 'pointer', transition: 'all 0.15s', width: '100%',
                      }}
                      onMouseEnter={e => { e.currentTarget.style.color = '#DC2626'; e.currentTarget.style.borderColor = '#FECACA'; e.currentTarget.style.background = '#FEF2F2'; }}
                      onMouseLeave={e => { e.currentTarget.style.color = T.text.muted; e.currentTarget.style.borderColor = '#E2E8F0'; e.currentTarget.style.background = T.bg.main; }}
                    >
                      Clear filters
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* -- Table card ------------------------------------------------- */}
            <div style={{
              background: T.bg.section, border: T.border, borderRadius: T.radius.xl,
              boxShadow: T.shadow.sm, overflow: 'hidden',
            }}>

              {/* Loading */}
              {loading && (
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr>
                        <th style={{ ...TH_BASE, width: 36 }} />
                        <th style={TH_BASE}>Student</th>
                        <th style={TH_BASE}>ID / Roll No</th>
                        <th style={TH_BASE}>Activity</th>
                        {!isAllTab && <th style={TH_BASE}>Score</th>}
                        <th style={TH_BASE}>Last Active</th>
                        <th style={TH_BASE}>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Array.from({ length: 8 }).map((_, i) => (
                        <SkeletonRow key={i} cols={colCount} />
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Error */}
              {!loading && error && (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '64px 24px', gap: 16, textAlign: 'center' }}>
                  <div style={{ width: 48, height: 48, borderRadius: T.radius.lg, background: '#FEF2F2', border: '1px solid #FECACA', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <AlertCircle size={20} style={{ color: '#DC2626' }} />
                  </div>
                  <div>
                    <p style={{ fontWeight: 700, color: T.text.heading, fontSize: '14px', margin: '0 0 4px' }}>Failed to load data</p>
                    <p style={{ fontSize: '13px', color: T.text.muted, maxWidth: 300, margin: 0 }}>{error}</p>
                  </div>
                  <button
                    onClick={fetchAnalytics}
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '9px 20px', borderRadius: T.radius.md, background: T.brand, color: '#FFF', border: 'none', cursor: 'pointer', fontSize: '13px', fontWeight: 700 }}
                  >
                    <RefreshCw size={13} /> Retry
                  </button>
                </div>
              )}

              {/* Empty */}
              {!loading && !error && sortedStudents.length === 0 && (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '64px 24px', gap: 12, textAlign: 'center' }}>
                  <div style={{ width: 48, height: 48, borderRadius: T.radius.lg, background: '#F1F5F9', border: T.border, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <BarChart2 size={22} style={{ color: '#CBD5E1' }} />
                  </div>
                  <p style={{ fontWeight: 700, color: T.text.heading, fontSize: '14px', margin: 0 }}>No students found</p>
                  <p style={{ fontSize: '13px', color: T.text.muted, maxWidth: 260, margin: 0 }}>
                    {hasFilters
                      ? 'Try adjusting or clearing your filters.'
                      : isAllTab ? 'No students are registered yet.' : `No ${activeTab} activity recorded yet.`}
                  </p>
                </div>
              )}

              {/* Data table */}
              {!loading && !error && sortedStudents.length > 0 && (
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr>
                        <th style={{ ...TH_BASE, width: 36 }} />
                        <SortableHeader label="Student"     field="name"        sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                        <th style={TH_BASE}>ID / Roll No</th>
                        <SortableHeader
                          label={isAllTab ? 'Total Activity' : 'Sessions'}
                          field="total"
                          sortField={sortField} sortDir={sortDir} onSort={handleSort}
                        />
                        {!isAllTab && (
                          <SortableHeader label="Avg Score" field="score" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                        )}
                        <SortableHeader label="Last Active" field="last_active" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                        <SortableHeader label="Status"      field="status"      sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                        {activeTab === 'BootCamp' && <th style={{ ...TH_BASE, width: 80, textAlign: 'center' }}>Actions</th>}
                      </tr>
                    </thead>

                    <tbody>
                      {sortedStudents.map((student) => {
                        const uid          = student.user_id || student.email || Math.random();
                        const isExpanded   = expandedIds.has(uid);
                        const totalCount   = safeInt(isAllTab ? student.total_activities : student.total_logs);
                        const displayStatus = isAllTab
                          ? (totalCount > 0 ? 'active' : null)
                          : student.overall_status || null;

                        return (
                          <React.Fragment key={uid}>
                            <tr
                              onClick={() => toggleExpand(uid)}
                              style={{
                                borderBottom: T.border, cursor: 'pointer',
                                background: isExpanded ? 'rgba(79, 70, 229,0.025)' : T.bg.section,
                                transition: 'background 0.12s',
                              }}
                              onMouseEnter={e => { if (!isExpanded) e.currentTarget.style.background = T.bg.main; }}
                              onMouseLeave={e => { if (!isExpanded) e.currentTarget.style.background = T.bg.section; }}
                            >
                              {/* Expand chevron */}
                              <td style={{ padding: '13px 14px 13px 16px', width: 36 }}>
                                {isExpanded
                                  ? <ChevronDown  size={14} style={{ color: T.brand }} />
                                  : <ChevronRight size={14} style={{ color: T.text.disabled }} />}
                              </td>

                              {/* Student */}
                              <td style={{ padding: '13px 16px' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                  <div style={{
                                    width: 32, height: 32, borderRadius: '50%',
                                    background: T.brandLight, flexShrink: 0,
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    fontSize: '12px', fontWeight: 800, color: T.brand,
                                  }}>
                                    {(student.first_name || '?')[0].toUpperCase()}
                                  </div>
                                  <div style={{ minWidth: 0 }}>
                                    <div style={{ fontWeight: 600, color: T.text.heading, fontSize: '13px', whiteSpace: 'nowrap' }}>
                                    {student.first_name || '-'}
                                    </div>
                                    <div
                                      style={{ fontSize: '11px', color: T.text.muted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 180, marginTop: 1 }}
                                      title={student.email || undefined}
                                    >
                                    {student.email || '-'}
                                    </div>
                                  </div>
                                </div>
                              </td>

                              {/* ID - truncated */}
                              <td style={{ padding: '13px 16px', minWidth: 160, maxWidth: 220 }}>
                                <span
                                  style={{
                                    fontSize: '12px', fontFamily: 'monospace',
                                    color: T.text.muted, background: T.bg.main,
                                    border: T.border, borderRadius: T.radius.sm,
                                    padding: '2px 8px', display: 'inline-block',
                                    wordBreak: 'break-all',
                                  }}
                                >
                                  {truncateId(student.user_id)}
                                </span>
                              </td>

                              {/* Activity count */}
                              <td style={{ padding: '13px 16px' }}>
                                <span style={{
                                  fontWeight: 700, fontSize: '14px',
                                  color: totalCount > 0 ? T.brand : T.text.disabled,
                                }}>
                                  {totalCount}
                                </span>
                                <span style={{ fontSize: '11px', color: T.text.muted, marginLeft: 4 }}>
                                  {isAllTab ? 'total' : `session${totalCount !== 1 ? 's' : ''}`}
                                </span>
                              </td>

                              {/* Avg score */}
                              {!isAllTab && (
                                <td style={{ padding: '13px 16px' }}>
                                  <ScoreBar score={student.avg_score} />
                                </td>
                              )}

                              {/* Last active */}
                              <td style={{ padding: '13px 16px', fontSize: '12px', whiteSpace: 'nowrap', color: T.text.muted }}>
                                {fmtDate(student.last_active)}
                              </td>

                              {/* Status */}
                              <td style={{ padding: '13px 16px' }}>
                                <StatusBadge status={displayStatus} />
                              </td>
                              {activeTab === 'BootCamp' && (
                                <td style={{ padding: '13px 14px', textAlign: 'center' }}>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleRemoveEnrollment(student.enrollment_id || student.logs?.[0]?.enrollment_id, student.first_name);
                                    }}
                                    disabled={removingId === (student.enrollment_id || student.logs?.[0]?.enrollment_id)}
                                    style={{
                                      background: 'none',
                                      border: 'none',
                                      padding: '6px',
                                      borderRadius: T.radius.sm,
                                      cursor: 'pointer',
                                      color: '#EF4444',
                                      display: 'inline-flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      transition: 'all 0.15s'
                                    }}
                                    onMouseEnter={e => { e.currentTarget.style.background = '#FEF2F2'; }}
                                    onMouseLeave={e => { e.currentTarget.style.background = 'none'; }}
                                    title="Remove Enrollment"
                                  >
                                    {removingId === (student.enrollment_id || student.logs?.[0]?.enrollment_id) ? (
                                      <RefreshCw size={14} className="animate-spin" />
                                    ) : (
                                      <Trash2 size={14} />
                                    )}
                                  </button>
                                </td>
                              )}
                            </tr>

                            {/* Expanded row */}
                            {isExpanded && (
                              <tr style={{ borderBottom: '1px solid rgba(79, 70, 229,0.10)' }}>
                                <td colSpan={colCount} style={{ padding: 0 }}>
                                  {isAllTab
                                    ? <FeatureBreakdown student={student} />
                                    : <LogsPanel student={student} />}
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* ── Pagination ─────────────────────────────────────────────────── */}
            {!loading && totalPages > 1 && (
              <div style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                flexWrap: 'wrap', gap: 12, marginTop: 14,
                background: T.bg.section, border: T.border, borderRadius: T.radius.xl,
                padding: '12px 20px', boxShadow: T.shadow.xs,
              }}>
                <p style={{ fontSize: '12px', color: T.text.muted, margin: 0 }}>
                  Showing{' '}
                  <b style={{ color: T.text.body }}>{(page - 1) * limit + 1}</b>–<b style={{ color: T.text.body }}>{Math.min(page * limit, total)}</b>
                  {' '}of{' '}
                  <b style={{ color: T.text.body }}>{total}</b> student{total !== 1 ? 's' : ''}
                </p>

                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <button
                    onClick={() => setPage(p => Math.max(p - 1, 1))}
                    disabled={page === 1}
                    style={{
                      padding: '6px 14px', fontSize: '12px', fontWeight: 700,
                      borderRadius: T.radius.md, border: T.border,
                      background: page === 1 ? T.bg.main : T.bg.section,
                      color: page === 1 ? T.text.disabled : T.brand,
                      cursor: page === 1 ? 'not-allowed' : 'pointer', transition: 'all 0.15s',
                    }}
                    onMouseEnter={e => { if (page !== 1) e.currentTarget.style.background = '#EFF6FF'; }}
                    onMouseLeave={e => { e.currentTarget.style.background = page === 1 ? T.bg.main : T.bg.section; }}
                  >
                    ← Prev
                  </button>

                  <span style={{ fontSize: '12px', fontWeight: 600, color: T.text.muted, padding: '0 4px' }}>
                    {page} / {totalPages}
                  </span>

                  <button
                    onClick={() => setPage(p => Math.min(p + 1, totalPages))}
                    disabled={page === totalPages}
                    style={{
                      padding: '6px 14px', fontSize: '12px', fontWeight: 700,
                      borderRadius: T.radius.md, border: T.border,
                      background: page === totalPages ? T.bg.main : T.bg.section,
                      color: page === totalPages ? T.text.disabled : T.brand,
                      cursor: page === totalPages ? 'not-allowed' : 'pointer', transition: 'all 0.15s',
                    }}
                    onMouseEnter={e => { if (page !== totalPages) e.currentTarget.style.background = '#EFF6FF'; }}
                    onMouseLeave={e => { e.currentTarget.style.background = page === totalPages ? T.bg.main : T.bg.section; }}
                  >
                    Next ->
                  </button>
                </div>
              </div>
            )}

          </div>
        </main>
      </div>

      <CollegeFooter />

      <style>{`
        footer[aria-label*="college Portal footer"] {
           transition: margin-left 300ms ease;
        }
        @keyframes spin  { to { transform: rotate(360deg); } }
        @keyframes pulse { 0%,100% { opacity:1; } 50% { opacity:0.45; } }
      `}</style>
    </div>
  );
};

export default CollegeAnalyticsDashboard;


